import { useState, Fragment, useEffect } from 'react';
import { TextSpan } from '../../../../Atoms/Text';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { PageLayout } from '../../../../PageLayout';
import { Toggle } from '../../../../Atoms/Toggle';
import { listSettings, updateSettingData } from '../../../../../Api/Settings/SettingsApi';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';

const settingInfo = {
  allowWriteToRpm: 'Allow write to RPM. This should only be enabled on production or during testing on dev.'
};

export const InstanceTab = () => {
  const settingInfoKeys = Object.keys(settingInfo);
  const [isUpdatingKey, setIsUpdatingKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState<{ [key: string]: boolean }>(
    settingInfoKeys.reduce(
      (acc, key) => {
        acc[key] = false;
        return acc;
      },
      {} as { [key: string]: boolean }
    )
  );

  const fetchSettings = async () => {
    const { data: settings } = await listSettings();
    if (settings) {
      setChecked(
        settingInfoKeys.reduce(
          (acc, key) => {
            acc[key] = settings.find((setting) => setting.key === key)?.active || false;
            return acc;
          },
          {} as { [key: string]: boolean }
        )
      );
    }
    setIsLoading(false);
  };

  const updateSetting = async (key: string, active: boolean) => {
    if (isUpdatingKey === key) return;
    setIsUpdatingKey(key);
    const { error } = await updateSettingData({ key, active });
    if (error) {
      setChecked((prev) => ({ ...prev, [key]: !active }));
    }
    setIsUpdatingKey(null);
  };

  useEffect(() => {
    setIsLoading(true);
    fetchSettings();
  }, []);

  const onChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked((prev) => ({ ...prev, [key]: e.target.checked }));
    updateSetting(key, e.target.checked);
  };

  return (
    <PageLayout
      className="w-full"
      dataTestId="instance-page"
      dataCy="instance-page-card"
      pageTitle="">
      <div data-testid="instance-tab">
        <PageCard
          title="Instance Settings"
          className="mb-5">
          <div className="flex p-5">
            {isLoading && (
              <div className="flex h-[10rem] m-auto">
                <ComponentSpinner label="Loading data..." />
              </div>
            )}
            {!isLoading &&
              Object.entries(settingInfo).map(([key, value], index) => {
                const id = `${key}-toggle`;
                return (
                  <Fragment key={index}>
                    <TextSpan className="flex items-center space-x-2">{value}</TextSpan>
                    <label
                      className="flex items-center ml-10 cursor-pointer"
                      htmlFor={id}>
                      <Toggle
                        checked={checked[key]}
                        onChange={onChange(key)}
                        id={id}
                        dataTestId={id}
                        className="flex ml-4"
                      />
                      <TextSpan className="ml-2 text-sm">{checked[key] ? 'Enabled' : 'Disabled'}</TextSpan>
                      {isUpdatingKey === key && (
                        <TextSpan className="ml-2 text-sm italic text-gray-500">Updating...</TextSpan>
                      )}
                    </label>
                  </Fragment>
                );
              })}
          </div>
        </PageCard>
      </div>
    </PageLayout>
  );
};
